import { LitElement } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { pimsterDialogStyle } from './pimster-dialog.style';
import pimsterDialogRender from './pimster-dialog.render';
import pimsterDialogConfig from './pimster-dialog.config';
import {
  getCustomTriggerElements,
  constructIframe,
  getTriggerPayloadFromCustomTriggerElement,
} from './pimster-dialog.utils';
import { PimsterEvents, Widget } from '../enums';
import { trackImpression } from '../analytics/events/impression';
import { trackOpen } from '../analytics/events/open';
import { IFRAME_ELEMENT_ID } from './pimster-dialog.constants';
import { logger } from '../utils/logger';
import { OpenDialogPayload } from './pimster-dialog.types';
import { InternalEvent } from '../lib/internal-events/enums';
import { isMessageInternalEvent } from '../lib/internal-events/validation/event';
import { sendPublicEvent } from '../lib/public-events/utils/send';
import { PublicEvent } from '../lib/public-events/enums';

const { name } = pimsterDialogConfig;

@customElement(name)
class PimsterDialog extends LitElement {
  // Properties
  @property({ type: String })
  iframeSrc = '';

  @property({ type: String })
  iframeTitle = '';

  @property({ type: Boolean })
  isOpen = false;

  static styles = pimsterDialogStyle;

  @query(`#${IFRAME_ELEMENT_ID}`)
  private iframe?: HTMLIFrameElement;

  // Lifecycle methods
  constructor() {
    super();
    this.isOpen = false;
  }

  connectedCallback() {
    super.connectedCallback();
    this.initialize = this.initialize.bind(this);
    this.initialize();
  }

  disconnectedCallback() {
    if (this.isOpen) {
      document.removeEventListener('keydown', this._watchEscape);
    }
    this._removeMessageListener();
    super.disconnectedCallback();
  }

  updated(changedProperties: Map<string, unknown>) {
    if (changedProperties.has('isOpen') && this.isOpen) {
      window.document.body.style.overflow = 'hidden';
    }
  }

  // Public methods
  initialize() {
    this._initializeCustomTriggers();
    this._attachMessageListener();
  }

  open({ widgetType, ...triggerPayload }: OpenDialogPayload) {
    const { src, title } = constructIframe(triggerPayload);
    this.isOpen = true;
    this.iframeSrc = src;
    this.iframeTitle = title;

    window.document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', this._watchEscape);

    trackOpen({ widgetType, ...triggerPayload });
  }

  close() {
    this.isOpen = false;
    window.document.body.style.overflow = '';
    document.removeEventListener('keydown', this._watchEscape);

    this._onClose();

    const closeEvent = new CustomEvent('dialog-closed');
    this.dispatchEvent(closeEvent);
  }

  render() {
    return pimsterDialogRender({
      isOpen: this.isOpen,
      iframe: { src: this.iframeSrc, title: this.iframeTitle },
      close: () => this.close(),
    });
  }

  // Private methods
  private _onClose() {
    if (!this.iframe?.contentWindow) {
      logger.error('No iframe window to post message to');
      return;
    }
    const srcURL = new URL(this.iframeSrc);
    const origin = `${srcURL.protocol}//${srcURL.host}`;
    this.iframe?.contentWindow?.postMessage({ action: 'pauseVideo' }, origin);
    this._resetIFrame();
  }

  private _resetIFrame() {
    this.iframeSrc = '';
    this.iframeTitle = '';
  }

  private _watchEscape = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      this.close();
    }
  };

  private _prefetch(payload: OpenDialogPayload) {
    const { src } = constructIframe(payload);
    if (document.querySelector(`link[href="${src}"][rel="prefetch"]`)) {
      return;
    }
    const link = document.createElement('link');
    link.href = src;
    link.rel = 'prefetch';
    link.as = 'iframe';
    document.head.appendChild(link);
  }

  private _initializeCustomTriggers() {
    const triggers = getCustomTriggerElements();
    triggers.forEach(trigger => {
      const triggerPayload = getTriggerPayloadFromCustomTriggerElement(trigger);
      trackImpression({
        widgetType: Widget.Custom,
        company: triggerPayload.company,
        product: triggerPayload.product,
        locale: triggerPayload.locale,
      });
      trigger.addEventListener(PimsterEvents.Click, () => {
        this.open({ widgetType: Widget.Custom, ...triggerPayload });
      });
    });
  }

  private _messageHandler(message: MessageEvent) {
    if (isMessageInternalEvent(message)) {
      const { action, payload } = message.data;

      switch (action) {
        case InternalEvent.ADD_TO_CART: {
          sendPublicEvent(PublicEvent.ADD_TO_CART, payload);
          break;
        }

        case InternalEvent.DIALOG_OPEN: {
          this.open(payload);
          break;
        }

        case InternalEvent.DIALOG_PREFETCH: {
          this._prefetch(payload);
          break;
        }

        case InternalEvent.DIALOG_CLOSE: {
          this.close();
          break;
        }

        default:
      }
    }
  }

  private _attachMessageListener() {
    window.addEventListener('message', this._messageHandler.bind(this));
  }

  private _removeMessageListener() {
    window.removeEventListener('message', this._messageHandler.bind(this));
  }
}

export default PimsterDialog;
